import {
  CheckCircleOutlineRounded,
  ErrorOutlineRounded,
  FiberManualRecordRounded,
} from "@mui/icons-material";
import { Box, Chip, CircularProgress, Stack, Tooltip } from "@mui/material";

import { ChatMessageWithParts } from "src/views/assistant/types";
import { useOnboardingStore } from "../../stores/OnboardingStore";
import OnboardingProgress from "../onboarding/OnboardingProgress";
import DialogueActions from "./DialogueActions";
import AgentDatasetAccessBanner from "./AgentDatasetAccessBanner";
import PersonaMetadata from "./PersonaMetadata";
import { PageTopHelpButton } from "src/components/help";
import {
  ConversationStatus,
  conversationStatusMeta,
} from "../conversationStatus";

type AtomAssistHeaderProps = {
  dialogueId: number | null;
  onNewChat: () => void;
  dialogue: ChatMessageWithParts[];
  conversationStatus?: ConversationStatus;
};

const AtomAssistHeader = ({
  dialogueId,
  onNewChat,
  dialogue,
  conversationStatus = "ready",
}: AtomAssistHeaderProps) => {
  const { isOnboarding } = useOnboardingStore();
  const statusMeta = conversationStatusMeta[conversationStatus];
  const statusColor =
    conversationStatus === "error"
      ? "error"
      : conversationStatus === "complete"
        ? "success"
        : conversationStatus === "working"
          ? "primary"
          : "default";
  const statusIcon =
    conversationStatus === "working" ? (
      <CircularProgress size={12} thickness={5} color="inherit" />
    ) : conversationStatus === "complete" ? (
      <CheckCircleOutlineRounded />
    ) : conversationStatus === "error" ? (
      <ErrorOutlineRounded />
    ) : (
      <FiberManualRecordRounded />
    );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        sx={{
          px: 2,
          width: "100%",
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: "0 1 auto" }}>
            <PersonaMetadata />
          </Box>
          <Tooltip title={`Conversation status: ${statusMeta.label}`} arrow>
            <Chip
              data-testid="aa-conversation-status"
              aria-label={`Conversation status: ${statusMeta.label}`}
              size="small"
              variant="outlined"
              color={statusColor}
              icon={statusIcon}
              label={statusMeta.label}
              sx={{
                height: 24,
                flexShrink: 0,
                fontWeight: 500,
                "& .MuiChip-icon": {
                  width: 14,
                  height: 14,
                  ml: { xs: "5px", sm: 1 },
                  color: "inherit",
                },
                "& .MuiChip-label": {
                  display: { xs: "none", sm: "block" },
                },
              }}
            />
          </Tooltip>
        </Box>

        <Box
          sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}
        >
          <PageTopHelpButton
            productLabel="AtomAssist"
            product="atomassist"
            compact
          />
          <DialogueActions
            dialogueId={dialogueId}
            onNewChat={onNewChat}
            dialogue={dialogue}
          />
        </Box>
      </Stack>
      <AgentDatasetAccessBanner />
      {isOnboarding && <OnboardingProgress />}
    </Box>
  );
};

export default AtomAssistHeader;
